// BUG-2026-0202 — The quote "Title" IS the project. Instead of a bare text box,
// offer a dropdown of existing projects (select → Title = ProjectName, so the
// accepted-quote conversion reuses that project by name), an "Add new project…"
// entry that opens the real New Project dialog (with its fuzzy duplicate
// warning), and — for free-typed titles — the same fuzzy "similar projects
// already exist" warning the New Project page shows.
import { useEffect, useMemo, useRef, useState } from 'react';
import type { SyntheticEvent } from 'react';
import { Autocomplete, Box, TextField, Typography, createFilterOptions } from '@mui/material';
import type { AutocompleteChangeReason } from '@mui/material';
import { Add } from '@mui/icons-material';
import { DuplicateWarning } from '../../components/forms/DuplicateWarning';
import { NewProjectDialog } from '../projects/NewProjectDialog';
import { getProjects } from '../../services/projectService';
import { getClients } from '../../services/clientService';
import { getSites } from '../../services/siteService';
import { usePermissions } from '../../hooks/usePermissions';
import type { Client, Project, Site } from '../../types/project';

/** Sentinel option for "Add new project…" (not a real project). */
const ADD_NEW_PROJECT = '__add_new_project__';
type AddNewOption = { ProjectKey: typeof ADD_NEW_PROJECT; ProjectName: string };
type PickerOption = Project | AddNewOption;

const isAddNew = (o: PickerOption | string): o is AddNewOption =>
  typeof o !== 'string' && o.ProjectKey === ADD_NEW_PROJECT;

/** Same normalisation DuplicateWarning uses, so "exact" means the same thing in both places. */
const normalize = (s: string) => s.toLowerCase().replace(/[\s._-]+/g, '');

const labelOf = (o: PickerOption | string | null): string =>
  o == null ? '' : typeof o === 'string' ? o : o.ProjectName;

const projectFilter = createFilterOptions<Project>({
  stringify: (p) => `${p.ProjectName} ${p.ProjectId}`,
});

interface ProjectTitlePickerProps {
  /** The persisted quote title (free text or an existing project's name). */
  value: string | null;
  /**
   * Fired when the title should be saved: on blur / Enter for free text, or
   * immediately when an existing project is selected or a new one is created
   * (then `project` is set so callers can also inherit e.g. its client).
   */
  onCommit: (title: string | null, project?: Project) => void;
  /** Live text callback (every keystroke) — for dialogs that keep local draft state. */
  onInput?: (text: string) => void;
  /** Enter pressed with the given text (after any commit) — dialogs use it to submit. */
  onEnter?: (text: string) => void;
  disabled?: boolean;
  label?: string;
  helperText?: string;
  required?: boolean;
  autoFocus?: boolean;
  error?: boolean;
}

export const ProjectTitlePicker = ({
  value, onCommit, onInput, onEnter, disabled, label = 'Project', helperText,
  required, autoFocus, error,
}: ProjectTitlePickerProps) => {
  const { canManageEntities } = usePermissions();
  const [projects, setProjects] = useState<Project[]>([]);
  const [inputValue, setInputValue] = useState(value ?? '');
  // True until the user types: a pristine field lists EVERY project when opened
  // (like MUI does for a selected value); typing switches to the filtered list.
  const [pristine, setPristine] = useState(true);
  const inputRef = useRef(inputValue);
  inputRef.current = inputValue;
  const [open, setOpen] = useState(false);
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [sites, setSites] = useState<Site[]>([]);

  const loadProjects = () =>
    getProjects({ pageSize: 9999 })
      .then((r) => setProjects(
        [...r.data].sort((a, b) => a.ProjectName.localeCompare(b.ProjectName, undefined, { sensitivity: 'base' })),
      ))
      .catch(() => {});

  useEffect(() => { loadProjects(); }, []);

  // Re-sync with the saved title when it changes from OUTSIDE (refetch / other
  // tab). A prop that merely echoes what's already typed (dialogs feeding
  // `onInput` straight back in as `value`) must not reset the field, or the
  // list would snap back to "unfiltered" on every keystroke.
  useEffect(() => {
    const next = value ?? '';
    if (next.trim() === inputRef.current.trim()) return;
    setInputValue(next);
    setPristine(true);
  }, [value]);

  // Backend POST /projects is admin-only — mirror the Projects page's gate so
  // the item never dead-ends on a 403.
  const canCreateProject = !disabled && canManageEntities;

  const options: PickerOption[] = useMemo(() => (
    canCreateProject
      ? [...projects, { ProjectKey: ADD_NEW_PROJECT, ProjectName: '+ Add new project…' } as AddNewOption]
      : projects
  ), [projects, canCreateProject]);

  /** Fuzzy-warning candidates: everything except an exact (normalised) match of what's typed. */
  const similarCandidates = useMemo(() => {
    const v = normalize(inputValue || '');
    return projects.filter((p) => normalize(p.ProjectName) !== v);
  }, [projects, inputValue]);

  const commit = (title: string, project?: Project) => {
    const t = title.trim();
    if (t === (value ?? '').trim() && !project) return;
    onCommit(t || null, project);
  };

  const openNewProject = () => {
    // Lazy-load the dialog's lookups only when someone actually adds a project.
    getClients().then(setClients).catch(() => {});
    getSites().then(setSites).catch(() => {});
    setNewProjectOpen(true);
  };

  const handleChange = (
    event: SyntheticEvent,
    next: PickerOption | string | null,
    reason: AutocompleteChangeReason,
  ) => {
    if (next != null && isAddNew(next)) { openNewProject(); return; }
    const text = labelOf(next);
    setInputValue(text);
    onInput?.(text);
    if (reason === 'clear') {
      // Backspacing to empty (or the ✕ button) mid-edit: only clear the text —
      // persisting Title=null here would race the refetch against what the
      // user types next. Blur commits the final value.
      return;
    }
    setPristine(true);
    commit(text, typeof next === 'object' && next !== null ? next : undefined);
    if (event.type === 'keydown' && onEnter) onEnter(text.trim());
  };

  const handleProjectCreated = (project: Project) => {
    setNewProjectOpen(false);
    loadProjects();
    setInputValue(project.ProjectName);
    setPristine(true);
    onInput?.(project.ProjectName);
    onCommit(project.ProjectName, project);
  };

  const isCurrent = (p: Project) => normalize(p.ProjectName) === normalize(inputValue);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Autocomplete<PickerOption, false, false, true>
        freeSolo
        size="small"
        fullWidth
        disabled={disabled}
        options={options}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        // `value` stays null on purpose: the text (inputValue) is the state we
        // own. Mirroring the title into `value` makes MUI treat the field as
        // pristine whenever text === label and silently drop the filter.
        value={null}
        inputValue={inputValue}
        onInputChange={(_e, text, reason) => {
          if (reason === 'input' || reason === 'clear') {
            setInputValue(text);
            setPristine(false);
            onInput?.(text);
          }
        }}
        onChange={handleChange}
        getOptionLabel={labelOf}
        filterOptions={(opts, state) => {
          const real = projectFilter(
            opts.filter((o): o is Project => !isAddNew(o)),
            { ...state, inputValue: pristine ? '' : state.inputValue },
          );
          const addNew = opts.find(isAddNew);
          return addNew ? [...real, addNew] : real;
        }}
        renderOption={(props, option) => {
          const { key, ...rest } = props as typeof props & { key: string };
          if (isAddNew(option)) {
            return (
              <Box key={key} component="li" {...rest} sx={{ color: 'primary.main', fontWeight: 600 }}>
                <Add sx={{ fontSize: 18, mr: 1 }} />
                Add new project…
              </Box>
            );
          }
          return (
            <Box key={key} component="li" {...rest} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important' }}>
              <Typography variant="body2" sx={{ fontWeight: isCurrent(option) ? 700 : 400 }}>
                {option.ProjectName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.ProjectId}{option.Status ? ` · ${option.Status}` : ''}
              </Typography>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            autoFocus={autoFocus}
            error={error}
            helperText={helperText}
            placeholder="Select an existing project or type a new title…"
            onBlur={() => { setPristine(true); commit(inputValue); }}
            onKeyDown={(e) => {
              // Popup closed ⇒ Autocomplete won't handle Enter; commit + submit ourselves.
              if (e.key === 'Enter' && !open) {
                commit(inputValue);
                onEnter?.(inputValue.trim());
              }
            }}
          />
        )}
      />
      {!disabled && (
        <DuplicateWarning
          value={inputValue}
          candidates={similarCandidates}
          nameSelector={(p) => p.ProjectName}
          secondarySelector={(p) => p.ProjectId}
          label="Similar projects already exist — pick one from the list to reuse it:"
        />
      )}
      <NewProjectDialog
        open={newProjectOpen}
        onClose={() => setNewProjectOpen(false)}
        onCreated={handleProjectCreated}
        initialClients={clients}
        initialSites={sites}
      />
    </Box>
  );
};
